import {Service, service, inject} from "./di";
import {i18n} from "../i18n/I18n";
import {LocationService} from "./LocationService";
import {SeoDataService} from "./SeoDataService";
import {BlackFridayService} from "./BlackFridayService";

@service
export default class MetaKeywordsService extends Service {
	@inject locationService: LocationService;
	@inject seoDataService: SeoDataService;
	@inject bf: BlackFridayService;

	setKeywords(keywords: string) {
		if (this.bf.isEnabled()) {
			keywords = `${this.getBlackFridayKeywords()}, ${keywords}`;
		}
		const element = document.querySelector("meta[name='keywords']");
		if (!element) {
			const meta = document.createElement("meta");
			meta.setAttribute("name", "keywords");
			meta.setAttribute("content", keywords);
			document.head.appendChild(meta);
		} else {
			element.setAttribute("content", keywords);
		}
	}

	getBlackFridayKeywords() {
		return i18n("bf_keywords", {
			country: this.getCountry(),
			year: new Date().getFullYear().toString()
		});
	}

	setBlackFridayPageKeywords() {
		const keywords = this.getBlackFridayPageKeywords();
		this.setKeywords(keywords);
	}

	getBlackFridayPageKeywords() {
		return i18n("keywords_blackfriday");
	}

	setHomePageKeywords() {
		const keywords = this.getHomePageKeywords();
		this.setKeywords(keywords);
	}

	getHomePageKeywords() {
		const data = { country: this.getCountry() };
		return i18n("keywords_homepage", data);
	}

	setCityPageKeywords() {
		const keywords = this.getCityPageKeywords();
		this.setKeywords(keywords);
	}

	getCityPageKeywords() {
		const data = {
			country: this.getCountry(),
			city: this.getCity(),
		};
		return i18n("keywords_citypage", data);
	}

	setFlyerPageKeywords(name: string) {
		const keywords = this.getFlyerPageKeywords(name);
		this.setKeywords(keywords);
	}

	getFlyerPageKeywords(name: string) {
		const data = {
			name,
			country: this.getCountry(),
		};
		return i18n("keywords_flyerpage", data);
	}

	getFlyersPageKeywords() {
		const data = {
			country: this.getCountry(),
			city: this.getCity(),
		};
		return i18n("keywords_flyers_page", data);
	}

	setStorePageKeywords(merchantName: string) {
		const keywords = this.getStorePageKeywords(merchantName);
		this.setKeywords(keywords);
	}

	getStorePageKeywords(merchant: string) {
		const data = {
			merchant,
			country: this.getCountry(),
			cities: this.getMerchantCities(),
		};
		return i18n("keywords_storepage", data);
	}

	setFlyersKeywords(category: string, city: string) {
		let keywords;
		if (category === "All Flyers") {
			keywords = this.getFlyersPageKeywords();
		} else {
			keywords = this.getFlyersCategoriesPageKeywords(category);
		}
		this.setKeywords(keywords);
	}

	getFlyersCategoriesPageKeywords(categoryName: string) {
		const data = {
			category: i18n(categoryName).toLowerCase(),
		};
		return i18n("keywords_flyers_categoriespage", data);
	}

	setItemPageKeywords(name: string, flyerName: string) {
		const keywords = this.getItemPageKeywords(name, flyerName);
		this.setKeywords(keywords);
	}

	getItemPageKeywords(name: string, flyerName: string) {
		const data = {
			name,
			flyerName,
			country: this.getCountry(),
		};
		return i18n("keywords_itempage", data);
	}

	setCouponsKeywords() {
		const keywords = this.getCouponsKeywords();
		this.setKeywords(keywords);
	}

	getCouponsKeywords() {
		const data = {
			country: this.getCountry(),
			city: this.getCity(),
		};
		return i18n("keywords_coupons", data);
	}

	setCouponsPageKeywords(name: string) {
		const keywords = this.getCouponsPageKeywords(name);
		this.setKeywords(keywords);
	}

	getCouponsPageKeywords(name: string) {
		const data = {
			name,
			country: this.getCountry(),
			city: this.getCity(),
		};
		return i18n("keywords_couponspage", data);
	}

	private getCountry() {
		const { locale } = this.locationService;
		return this.locationService.isUsLocale(locale) ? "USA" : "Canada";
	}

	private getCity() {
		return this.locationService.city;
	}

	private getMerchantCities() {
		const { locale } = this.locationService;
		const cities = this.locationService.isUsLocale(locale) ?
			this.seoDataService.getTopUsCities() :
			this.seoDataService.getTopCaCities();
		return cities.map((city: any) => city.name).join(", ");
	}
}
